import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPlacements } from 'core/actions/ocapi';
import isEmpty from 'lodash/isEmpty';
import { selectBrowserIsMobile } from 'core/selectors/user';

import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import SectionTitle from 'core/modules/SectionTitle/SectionTitle';
import BonusTiers from 'core/modules/BonusTiers/BonusTiers';
import SimpleBannerItem from './components/SimpleBannerItem/SimpleBannerItem';
import StylizedBannerItem from './components/StylizedBannerItem/StylizedBannerItem';

import {
  selectSimpleBannerListConfig,
  makeSelectSimpleBannerListData,
  makeSelectSimpleBannerListIsLoaded,
  makeSelectSimpleBannerListIsLoading,
} from './selectors';

import './SimpleBannerList.scss';

const VIEW_TYPE_1_ACROSS = '1_across';
const VIEW_TYPE_2_ACROSS = '2_across';
const VIEW_TYPE_3_ACROSS = '3_across';
const VIEW_TYPE_4_ACROSS = '4_across';

const MOBILE_CONTENT_ITEM_TYPE = 86;
const DESKTOP_CONTENT_ITEM_TYPE = 64;
const STYLIZED_BANNER_CONTENT_ITEM_TYPE = 91;

function SimpleBannerList({
  namespace,
  item_display: itemDisplay,
  content_group_id,
  section_title: title,
  include_bonus_details,
}) {
  const selectSimpleBannerListData = makeSelectSimpleBannerListData();
  const selectSimpleBannerListIsLoading = makeSelectSimpleBannerListIsLoading();
  const selectSimpleBannerListIsLoaded = makeSelectSimpleBannerListIsLoaded();

  const config = useSelector(selectSimpleBannerListConfig);
  const isLoading = useSelector(state => selectSimpleBannerListIsLoading(state, namespace));
  const isLoaded = useSelector(state => selectSimpleBannerListIsLoaded(state, namespace));
  let data = useSelector(state => selectSimpleBannerListData(state, namespace));
  const isMobile = useSelector(selectBrowserIsMobile);

  const dispatch = useDispatch();

  /**
   * API only returns 3 across and 4 across optionsopenInNewWindow
   * we have to change the view based on length of the response for 1 across/2 across
   * @param data
   * @param simpleBannerListLength
   * @returns {{simpleBannerClass: *, simpleBannerItems: *}}
   */
  const getSimpleBannerListData = (data, simpleBannerListLength) => {
    const stylizedBannerItem = data.find(item => item.contentItemTypeId === STYLIZED_BANNER_CONTENT_ITEM_TYPE);
    if (stylizedBannerItem) {
      return {
        simpleBannerClass: VIEW_TYPE_1_ACROSS,
        simpleBannerItems: [stylizedBannerItem],
      };
    }

    let simpleBannerClass = VIEW_TYPE_1_ACROSS; // default
    let simpleBannerItems = data.slice(0, 1); // default

    if (itemDisplay === VIEW_TYPE_4_ACROSS && simpleBannerListLength > 3) {
      simpleBannerClass = VIEW_TYPE_4_ACROSS;
      simpleBannerItems = data.slice(0, 4);
    } else if (itemDisplay === VIEW_TYPE_3_ACROSS && simpleBannerListLength > 2) {
      simpleBannerClass = VIEW_TYPE_3_ACROSS;
      simpleBannerItems = data.slice(0, 3);
    } else if (itemDisplay === VIEW_TYPE_2_ACROSS && simpleBannerListLength > 1) {
      simpleBannerClass = VIEW_TYPE_2_ACROSS;
      simpleBannerItems = data.slice(0, 2);
    }

    return {
      simpleBannerClass,
      simpleBannerItems,
    };
  };

  const callFetchPlacements = (contentTypeId) => {
    const { api: { params, options } } = config;

    // In order to support the stylized banner response we should add it's item type to requests
    // for components with a property set item_display: '1_across'
    const contentTypeIdsArr = [contentTypeId];
    if (itemDisplay === VIEW_TYPE_1_ACROSS) {
      contentTypeIdsArr.push(STYLIZED_BANNER_CONTENT_ITEM_TYPE);
    }
    const paramsWithContentGroupAndType = {
      content_type_id: contentTypeIdsArr.join(),
      content_group_id,
      ...params,
    };

    if (!paramsWithContentGroupAndType.content_group_id) {
      return false; // content_group_id param is required for /placements API endpoint
    }

    dispatch(fetchPlacements(paramsWithContentGroupAndType, { ...options, namespace }));
    return true;
  };

  useEffect(() => {
    if (!isLoaded && !isLoading) {
      const contentTypeId = isMobile ? MOBILE_CONTENT_ITEM_TYPE : DESKTOP_CONTENT_ITEM_TYPE;
      callFetchPlacements(contentTypeId);
    }
  }, []);

  useEffect(() => {
    if (isLoaded && isMobile && data.length === 0) {
      callFetchPlacements(DESKTOP_CONTENT_ITEM_TYPE);
    }
  }, [isLoaded]);

  // Data loading tracking should be refactored in scope of CLPL-16903
  if (isEmpty(data)) {
    return isLoaded && include_bonus_details === '1' ? <BonusTiers /> : null;
  }

  const simpleBannerData = getSimpleBannerListData(data, data.length);
  const simpleBannerListClassName =
    `mn_simpleBannerList mn_clearfix mn_simpleBanner${simpleBannerData.simpleBannerClass}`;
  data = simpleBannerData.simpleBannerItems;

  return (
    <div className={simpleBannerListClassName} data-test="simple-banner-list">
      {title && <SectionTitle title={title} useHtmlMarkup />}
      {data && data.map(dataItem => (
        dataItem.contentItemTypeId === STYLIZED_BANNER_CONTENT_ITEM_TYPE // is stylized banner item type
          ? <StylizedBannerItem
              key={dataItem.dealId}
              dataItem={dataItem}
              hasBonus={include_bonus_details === '1'}
          />
          : <SimpleBannerItem key={dataItem.dealId} dataItem={dataItem} />
      ))}
    </div>
  );
}

SimpleBannerList.defaultProps = {
  namespace: 'default',
};

export default (props) => <ComponentErrorBoundary><SimpleBannerList {...props} /></ComponentErrorBoundary>;
